import * as turf from '@turf/turf';
import * as utm from 'utm';
import L from 'leaflet';
//@ts-ignore
import proj4 from 'proj4';
import { theme } from '../theme/theme';
import {
    Coordinates,
    CoordinatesArray,
    CoordinatesWithElevation,
    CoordinatesWithElevationVer2,
    DirectionalPoints,
    DirectionalSlopes,
    PointCoordinate,
} from '../types/types';
import { Position } from '@turf/turf';

export const radius = function (x: number) {
    return (x * Math.PI) / 180;
};

/*---------------------------------------TURF FUNCTIONS------------------------------------*/
export const getDistance = function (p1: Position, p2: Position) {
    var fromPointObject = turf.point(p1);
    var toPointObject = turf.point(p2);
    var options: any = { units: 'meters' };

    var distance = turf.distance(fromPointObject, toPointObject, options);
    return distance;
};

export const getLength = (line: Coordinates[]) => {
    const polylineCoordsArray: Position[] = convertPolylineCoordsToLatLngArray(line);
    if (polylineCoordsArray) {
        var lineObject: turf.Feature<turf.LineString, turf.Properties> = turf.lineString(polylineCoordsArray);
        var length = turf.length(lineObject, { units: 'meters' });
        return length;
    }
};
export const isPointInPolygon = (polygon: Position[], point: Coordinates) => {
    var pointObject = turf.point([point.lat, point.lng]);
    var polygonObject = turf.polygon([polygon]);

    return turf.booleanPointInPolygon(pointObject, polygonObject);
};

export const calculateDistanceOfPointToPolygon = (polygon: Coordinates[], markedPoint: Coordinates) => {
    const route: number[][] = [];
    polygon.forEach((element: Coordinates) => {
        route.push([element.lng, element.lat]);
    });

    const pointObject = turf.point([markedPoint.lng, markedPoint.lat]);

    let polygonLine = turf.lineString(route);
    var nearest = turf.nearestPointOnLine(polygonLine, pointObject, {
        units: 'meters',
    });
    if (nearest.properties.dist) {
        return nearest.properties.dist;
    } else return 0;
};

export const calculateArea = (polygon: Coordinates[]) => {
    const polygonCoordsArray: Position[] = convertPolygonCoordsToLngLatArray(polygon);
    const polygonObject = turf.polygon([polygonCoordsArray]);
    return turf.area(polygonObject);
};

export const calculatePerimeter = (polygon: Coordinates[]) => {
    const polygonCoordsArray: Position[] = convertPolygonCoordsToLngLatArray(polygon);
    const linesObject = turf.lineString(polygonCoordsArray);
    const perimeter = turf.length(linesObject, { units: 'meters' });
    return perimeter;
};
export const calculateCenterOfPolyline = (polyline: Coordinates[]) => {
    if (polyline) {
        const polylineCoordsArray: Position[] = convertPolygonCoordsToLngLatArray(polyline);
        var pointsObject = turf.points(polylineCoordsArray);
        var center = turf.center(pointsObject);
        return center;
    }
};

export const calculateCenterOfPolygon = (polygon: Coordinates[]) => {
    if (polygon.length >= 3) {
        const polygonCoordsArray: Position[] = convertPolygonCoordsToLatLngArray(polygon);
        const polygonObject = turf.polygon([polygonCoordsArray]);
        var centerObject = polygonCoordsArray && turf.centerOfMass(polygonObject);
        var centerCoords = { lat: centerObject.geometry.coordinates[0], lng: centerObject.geometry.coordinates[1] };
        return centerCoords;
    }
    return;
};

export const calculateZoomLevel = (polygon: Coordinates[]) => {
    const polygonCoordsArray = convertPolygonCoordsToLatLngArray(polygon);
    // Create a polygon feature from the coordinates
    const polygonObject = turf.polygon([polygonCoordsArray]);

    // Calculate the bounding box of the polygon
    const [west, south, east, north] = turf.bbox(polygonObject);
    let angle = east - west;
    if (angle < 0) {
        angle += 360;
    }

    var zoom = Math.round(Math.log(360 / angle) / Math.LN2);
    return zoom;
};

export const isPolygonContainedWithinBounds = (checkPolygon: Coordinates[], drawnPolygon: Coordinates[]) => {
    // console.log('CHECK POLYGON: ', checkPolygon);
    // console.log('DRAWN POLYGON: ', drawnPolygon);
    if (checkPolygon && drawnPolygon) {
        const checkPolygonCoordsArray: any = convertPolygonCoordsToLatLngArray(checkPolygon);
        const drawnPolygonCoordsArray: any = convertPolygonCoordsToLatLngArray(drawnPolygon);

        var checkPolygonObject = turf.polygon([checkPolygonCoordsArray]);
        var drawnPolygonObject = turf.polygon([drawnPolygonCoordsArray]);

        return turf.booleanContains(checkPolygonObject, drawnPolygonObject);
    }
};

/*-----------------------------------------CUSTOM FUNCTIONS-------------------------------------------*/
export const generatePointsBetweenGivenPoints = (points: CoordinatesWithElevation[]) => {
    const lastPoint = points[1];
    // console.log('elevation points line: ', lastPoint);
    let distanceBetweenPoints = 0.1;
    const lineCoordsArray: Position[] = convertElevationPointsToLngLatArray(points);
    // Generate points along the polygon border
    const lineString = turf.lineString(lineCoordsArray);
    const linePoints = turf.lineChunk(lineString, distanceBetweenPoints, { units: 'meters' });
    const extractedLineCoordinates = linePoints.features.map((feature) => feature.geometry.coordinates);
    const generatedlineCoordinates = extractedLineCoordinates.map((coords) => {
        return { lat: coords[0][1], lng: coords[0][0] };
    });

    // const test = [...generatedlineCoordinates, lastPoint];
    // console.log('test: ', test);
};

export const generatePointsInPolygon = (polygonCoords: Coordinates[], pointsDistance?: number) => {
    const landPolygonCoordsArray = convertPolygonCoordsToLngLatArray(polygonCoords);
    // landPolygonCoordsArray?.pop();
    const numPoints = 1000;
    const polygon = turf.polygon([landPolygonCoordsArray]);

    // Calculate the bounding box of the polygon
    const bbox = turf.bbox(polygon);

    // Calculate the average distance between points based on the desired number of points
    const distanceBetweenPoints = Math.sqrt(turf.area(polygon) / numPoints);

    // Generate points along the polygon border
    const lineString = turf.lineString(landPolygonCoordsArray);
    const polygonBorderPoints = turf.lineChunk(lineString, pointsDistance ? pointsDistance : distanceBetweenPoints, {
        units: 'meters',
    });

    // Generate a point grid within the polygon using Turf.js
    const grid = turf.pointGrid(bbox, pointsDistance ? pointsDistance : distanceBetweenPoints, {
        units: 'meters',
        mask: polygon,
    });

    const generatedPointsWithinPolygonCoordinatesLatLng: Coordinates[] = [];
    const generatedPointsWithinPolygonCoordinatesArray: any = [];
    const generatedPolygonBorderCoordinatesLatLng: Coordinates[] = [];
    const generatedPolygonBorderCoordinatesArray: any = [];

    //Convert and extract the grid coordinates
    grid.features.map((feature) => {
        generatedPointsWithinPolygonCoordinatesLatLng.push({
            lat: feature.geometry.coordinates[1],
            lng: feature.geometry.coordinates[0],
        });
        generatedPointsWithinPolygonCoordinatesArray.push([
            feature.geometry.coordinates[1],
            feature.geometry.coordinates[0],
        ]);
    });
    //Convert and extract the border coordinates
    polygonBorderPoints.features.map((feature) => {
        generatedPolygonBorderCoordinatesLatLng.push({
            lat: feature.geometry.coordinates[0][1],
            lng: feature.geometry.coordinates[0][0],
        });
        generatedPolygonBorderCoordinatesArray.push([
            feature.geometry.coordinates[0][1],
            feature.geometry.coordinates[0][0],
        ]);
    });

    // Combine the border points lat lngs with the inner points lat lngs
    var generatedPointsCoordinatesLatLngs = [
        ...generatedPointsWithinPolygonCoordinatesLatLng,
        ...generatedPolygonBorderCoordinatesLatLng,
    ];

    // Combine the border points array with the inner points array
    var generatedPointsCoordinatesArray = [
        ...generatedPointsWithinPolygonCoordinatesArray,
        ...generatedPolygonBorderCoordinatesArray,
    ];

    // console.log('generated points array: ', generatedPointsWithinPolygonCoordinatesArray);
    // console.log('generated points lat lngs:', generatedPointsCoordinatesLatLngs);
    return { generatedPointsCoordinatesLatLngs, generatedPointsCoordinatesArray, generatedPolygonBorderCoordinatesLatLng };
};

export const generateSquareGridInPolygon = (polygonCoords: Coordinates[], gridCellSize?: number) => {
    console.log('grid cell size check: ', gridCellSize);
    const polygonCoordsArray = convertPolygonCoordsToLngLatArray(polygonCoords);
    // const numGridCells = 100; // Change this as needed
    const polygon = turf.polygon([polygonCoordsArray]);
    console.log('polygon check: ', polygon);

    // Calculate the bounding box of the polygon
    const bbox = turf.bbox(polygon);
    console.log('bbox check: ', bbox);
    // Calculate the area of the polygon
    const area = turf.area(polygon);

    // Calculate the area of a single cell based on the provided gridCellSize or use a default size
    const cellSize = gridCellSize || 1000; // Default cell size if not provided
    const cellArea = cellSize * cellSize;
    const generatedGridCoordinatesLatLng: any = [];

    // Calculate the number of grid cells
    const numGridCells = Math.ceil(area / cellArea);
    if (numGridCells <= 210000) {
        // Generate a square grid within the polygon using Turf.js
        const grid = turf.squareGrid(bbox, gridCellSize ? gridCellSize : 1000, {
            units: 'meters',
            mask: polygon,
        });

        // const generatedGridCoordinatesArray: any[] = [];

        // Convert and extract the grid coordinates
        grid.features.forEach((feature) => {
            generatedGridCoordinatesLatLng.push(
                feature.geometry.coordinates[0].map((coord: Position) => [coord[1], coord[0]]),
            );
        });
        // grid.features.map((feature) => {
        //     generatedGridCoordinatesLatLng.push(feature.geometry.coordinates[0].map((coord: Position) => [coord[1], coord[0]]));
        //     // generatedGridCoordinatesArray.push([feature.geometry.coordinates[1], feature.geometry.coordinates[0]]);
        // });

        return { generatedGridCoordinatesLatLng, success: true, singleCellArea: cellArea };
    }
    return { generatedGridCoordinatesLatLng, success: false };
};

export const generateSquareGridDataObjectsInRegion = (polygonCoords: any, gridCellSize?: number) => {
    const polygon = turf.polygon([polygonCoords]);
    const bbox = turf.bbox(polygon);

    // Calculate the area of a single cell based on the provided gridCellSize or use a default size
    const cellSize = gridCellSize || 1000; // Default cell size if not provided
    const cellArea = cellSize * cellSize;
    const generatedGridCellsData: any = [];

    // Set the grid cell size to ensure square grid cells
    const grid = turf.squareGrid(bbox, 5000, {
        units: 'meters',
        //@ts-ignore
        mask: polygon,
    });
    console.log('grid check: ', grid);

    // Convert and extract the grid coordinates
    grid.features.forEach((feature, index) => {
        // Get the centroid of each grid cell
        var centroid = turf.centroid(feature);

        // Convert the centroid coordinates to UTM
        var utmCoords = utm.fromLatLon(centroid.geometry.coordinates[1], centroid.geometry.coordinates[0]);

        // Extract UTM zone information
        var utmZone = utmCoords.zoneNum;
        var utmLetter = utmCoords.zoneLetter;

        // var center = centroid;

        generatedGridCellsData.push({
            id: index + 1,
            utmZone,
            utmLetter,
            center: centroid.geometry.coordinates,
            coordinates: feature.geometry.coordinates[0].map((coord: Position) => [coord[1], coord[0]]),
        });
    });
    return { generatedGridCellsData, success: true, singleCellArea: cellArea };
};

export const findHighestAndLowestElevationPoints = (points: CoordinatesWithElevationVer2[]) => {
    let highest = points[0];
    let lowest = points[0];

    for (let i = 0; i < points.length - 1; i++) {
        if (points[i].elevation > highest.elevation) {
            highest = points[i];
        }
        if (points[i].elevation < lowest.elevation) {
            lowest = points[i];
        }
    }
    console.log('highest/lowest points: ', { highest: highest, lowest: lowest });
    return { highest, lowest };
};

// Haversine formula implementation (using metric units)
function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
    const R = 6371e3; // Radius of the Earth in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = R * c;
    return distance;
}

export function convertCoordToDMS(coord: number) {
    const absoluteCoord = Math.abs(coord);
    const degrees = Math.floor(absoluteCoord);
    const minutesDecimal = (absoluteCoord - degrees) * 60;
    const minutes = Math.floor(minutesDecimal);
    const seconds = ((minutesDecimal - minutes) * 60).toFixed(2);

    return {
        degrees: degrees,
        minutes: minutes,
        seconds: seconds,
    };
}

export function convertCoordsToUTM(coords: Coordinates) {
    // Example latitude and longitude coordinates
    // const latitude = coords.lat;
    // const longitude = coords.lng;
    // Determine the appropriate UTM zone based on the longitude
    // const zone = Math.floor((longitude + 180) / 6) + 1;
    // Define the UTM projection string dynamically
    // const utmProjection = `+proj=utm +zone=${zone} +ellps=WGS84 +datum=WGS84 +units=m +no_defs`;
    // Convert latitude and longitude to UTM easting and northing
    // const utmCoords = proj4('EPSG:4326', utmProjection, [longitude, latitude]);
    // const easting = Math.round(utmCoords[0]);
    // const northing = Math.round(utmCoords[1]);

    const utmZone = utm.fromLatLon(coords.lat, coords.lng);

    return {
        // x: easting,
        // y: northing,
        utmZone,
    };
}

export const filterPolygonPointsByDistance = (latlngs: Coordinates[]) => {
    const points = latlngs.map((latlng) => [latlng.lat, latlng.lng]);

    const filteredPoints = [points[0]];

    const minDistanceCentimeters = 50; // Set the minimum distance between points in centimeters

    for (let i = 1; i < points.length; i++) {
        const prevPoint = filteredPoints[filteredPoints.length - 1];
        const point = points[i];

        const distanceCentimeters = calculateDistanceInCentimeters(prevPoint, point); // Calculate the distance between points in centimeters

        if (distanceCentimeters > minDistanceCentimeters) {
            filteredPoints.push(point);
        }
    }
    const filteredPointsObjects = filteredPoints.map((point) => new L.LatLng(point[0], point[1]));
    return filteredPointsObjects;
};

// Function to calculate the distance between two points in centimeters
const calculateDistanceInCentimeters = (point1: PointCoordinate, point2: PointCoordinate) => {
    const lat1 = point1[0];
    const lon1 = point1[1];
    const lat2 = point2[0];
    const lon2 = point2[1];

    const earthRadius = 6371000; // Earth's radius in meters

    // Convert latitude and longitude to radians
    const lat1Rad = lat1 * (Math.PI / 180);
    const lat2Rad = lat2 * (Math.PI / 180);
    const lon1Rad = lon1 * (Math.PI / 180);
    const lon2Rad = lon2 * (Math.PI / 180);

    // Haversine formula
    const deltaLat = lat2Rad - lat1Rad;
    const deltaLon = lon2Rad - lon1Rad;
    const a = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    // Distance in meters
    const distanceMeters = earthRadius * c;

    // Convert distance to centimeters
    const distanceCentimeters = distanceMeters * 100;

    return distanceCentimeters;
};

export const convertPolylineCoordsToLatLngArray = (polylineCoords: Coordinates[]) => {
    if (polylineCoords.length > 1) {
        let polylineCoordsArray: Position[] = [];
        polylineCoords.map((coord) => {
            polylineCoordsArray.push([coord.lat, coord.lng]);
        });

        return polylineCoordsArray;
    } else return [];
};

export const convertPolylineCoordsToLngLatArray = (polylineCoords: Coordinates[]) => {
    if (polylineCoords.length > 1) {
        let polylineCoordsArray: Position[] = [];
        polylineCoords.map((coord) => {
            polylineCoordsArray.push([coord.lat, coord.lng]);
        });

        return polylineCoordsArray;
    } else return [];
};

export const convertElevationPointsToLatLngObject = (elevationPoints: CoordinatesWithElevationVer2[]) => {
    if (elevationPoints.length > 1) {
        let elevationPointsObjectArray: CoordinatesWithElevation[] = [];
        elevationPoints.map((point) => {
            elevationPointsObjectArray.push({ lat: point.latitude, lng: point.longitude, elevation: point.elevation });
        });

        return elevationPointsObjectArray;
    }
};

export const convertElevationPointsToLngLatArray = (elevationPoints: CoordinatesWithElevation[]) => {
    if (elevationPoints.length > 1) {
        let elevationPointsArray: Position[] = [];
        elevationPoints.map((point) => {
            elevationPointsArray.push([point.lng, point.lat]);
        });

        return elevationPointsArray;
    } else return [];
};

export const convertPolygonCoordsToLatLngArray = (polygonCoords: Coordinates[]) => {
    if (polygonCoords) {
        if (polygonCoords.length > 0) {
            let polygonCoordsArray: Position[] = [];
            polygonCoords.map((coord) => {
                polygonCoordsArray.push([coord.lat, coord.lng]);
            });
            //push the first coordinates lat lng to end of the coords array so it works with turf
            polygonCoordsArray.push([polygonCoordsArray[0][0], polygonCoordsArray[0][1]]);

            return polygonCoordsArray;
        } else {
            return [];
        }
    } else {
        return [];
    }
};

export const convertPolygonCoordsToLatLngElevationArray = (polygonCoords: CoordinatesWithElevation[]) => {
    if (polygonCoords) {
        if (polygonCoords.length > 0) {
            let polygonCoordsArray: any = [];
            polygonCoords.map((coord) => {
                polygonCoordsArray.push({ point: [coord.lat, coord.lng], elevation: coord.elevation });
            });
            //push the first coordinates lat lng to end of the coords array so it works with turf
            // polygonCoordsArray.push([polygonCoordsArray[0][0], polygonCoordsArray[0][1]]);

            return polygonCoordsArray;
        } else {
            return [];
        }
    } else {
        return [];
    }
};

export const convertPolygonCoordsToLngLatArray = (polygonCoords: Coordinates[]) => {
    if (polygonCoords) {
        if (polygonCoords.length > 0) {
            let polygonCoordsArray: Position[] = [];
            polygonCoords.map((coord) => {
                polygonCoordsArray.push([coord.lng, coord.lat]);
            });
            //push the first coordinates lat lng to end of the coords array so it works with turf
            polygonCoordsArray.push([polygonCoordsArray[0][0], polygonCoordsArray[0][1]]);

            return polygonCoordsArray;
        } else {
            return [];
        }
    } else return [];
};

export function calculateSlope(point1: CoordinatesWithElevation, point2: CoordinatesWithElevation, color: string = '') {
    const rise = point1.elevation - point2.elevation;

    // Calculate the run using the Haversine formula
    const run = haversineDistance(point1.lat, point1.lng, point2.lat, point2.lng);

    // Calculate the slope percentage
    const slopePercentage = (rise / run) * 100;

    // Calculate the slope angle in degrees using inverse tangent (arctan)
    const slopeAngle = Math.atan(slopePercentage / 100) * (180 / Math.PI);

    const slopeDirection = calculateSlopeDirection(point1, point2);

    const slopePointsDistance = getDistance([point1.lat, point1.lng], [point2.lat, point2.lng]);
    return { slopePercentage, slopeAngle, slopeDirection, slopePointsDistance, color };
}

export function calculateDirectionalSlopes(highestPoints: DirectionalPoints, lowestPoints: DirectionalPoints) {
    //@ts-ignore
    let slopes: DirectionalSlopes = {};

    slopes.North = calculateSlope(highestPoints.North, lowestPoints.South, theme.colors.directions.north);
    slopes.Northeast = calculateSlope(highestPoints.Northeast, lowestPoints.Southwest, theme.colors.directions.northeast);
    slopes.East = calculateSlope(highestPoints.East, lowestPoints.West, theme.colors.directions.east);
    slopes.Southeast = calculateSlope(highestPoints.Southeast, lowestPoints.Northwest, theme.colors.directions.southeast);
    slopes.South = calculateSlope(highestPoints.South, lowestPoints.North, theme.colors.directions.south);
    slopes.Southwest = calculateSlope(highestPoints.Southwest, lowestPoints.Northeast, theme.colors.directions.southwest);
    slopes.West = calculateSlope(highestPoints.West, lowestPoints.East, theme.colors.directions.west);
    slopes.Northwest = calculateSlope(highestPoints.Northwest, lowestPoints.Southeast, theme.colors.directions.northwest);

    return slopes;
}

export function calculateSlopeDirection(point1: CoordinatesWithElevation, point2: CoordinatesWithElevation) {
    let lat1 = point1.lat;
    let lon1 = point1.lng;
    let elevation1 = point1.elevation;
    let lat2 = point2.lat;
    let lon2 = point2.lng;
    let elevation2 = point2.elevation;

    const degreesToRadians = Math.PI / 180;
    const radiansToDegrees = 180 / Math.PI;

    // Convert coordinates to radians
    const lat1Rad = lat1 * degreesToRadians;
    const lon1Rad = lon1 * degreesToRadians;
    const lat2Rad = lat2 * degreesToRadians;
    const lon2Rad = lon2 * degreesToRadians;

    // Calculate difference in longitude
    const deltaLon = lon2Rad - lon1Rad;

    // Calculate slope azimuth
    const azimuthRad = Math.atan2(
        Math.sin(deltaLon) * Math.cos(lat2Rad),
        Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(deltaLon),
    );

    // Convert azimuth from radians to degrees
    let azimuthDeg = azimuthRad * radiansToDegrees;

    // Adjust azimuth range to be between 0 and 360 degrees
    if (azimuthDeg < 0) {
        azimuthDeg += 360;
    }

    // Determine slope direction based on azimuth angle and elevation comparison
    let direction = '';
    let degree = 0;

    if (elevation1 > elevation2) {
        if (azimuthDeg >= 337.5 || azimuthDeg < 22.5) {
            direction = 'North';
        } else if (azimuthDeg >= 22.5 && azimuthDeg < 67.5) {
            direction = 'Northeast';
        } else if (azimuthDeg >= 67.5 && azimuthDeg < 112.5) {
            direction = 'East';
        } else if (azimuthDeg >= 112.5 && azimuthDeg < 157.5) {
            direction = 'Southeast';
        } else if (azimuthDeg >= 157.5 && azimuthDeg < 202.5) {
            direction = 'South';
        } else if (azimuthDeg >= 202.5 && azimuthDeg < 247.5) {
            direction = 'Southwest';
        } else if (azimuthDeg >= 247.5 && azimuthDeg < 292.5) {
            direction = 'West';
        } else if (azimuthDeg >= 292.5 && azimuthDeg < 337.5) {
            direction = 'Northwest';
        }
        degree = azimuthDeg;
    } else if (elevation1 < elevation2) {
        if (azimuthDeg >= 337.5 || azimuthDeg < 22.5) {
            direction = 'South';
        } else if (azimuthDeg >= 22.5 && azimuthDeg < 67.5) {
            direction = 'Southwest';
        } else if (azimuthDeg >= 67.5 && azimuthDeg < 112.5) {
            direction = 'West';
        } else if (azimuthDeg >= 112.5 && azimuthDeg < 157.5) {
            direction = 'Northwest';
        } else if (azimuthDeg >= 157.5 && azimuthDeg < 202.5) {
            direction = 'North';
        } else if (azimuthDeg >= 202.5 && azimuthDeg < 247.5) {
            direction = 'Northeast';
        } else if (azimuthDeg >= 247.5 && azimuthDeg < 292.5) {
            direction = 'East';
        } else if (azimuthDeg >= 292.5 && azimuthDeg < 337.5) {
            direction = 'Southeast';
        }
        degree = (azimuthDeg + 180) % 360; // Adjust degree for opposite direction
    }
    return { direction, degree };
}

export function findHighestDirectionalPoints(points: CoordinatesWithElevationVer2[], polygonCoordinates: Coordinates[]) {
    const highestPoints = {
        North: { elevation: -Infinity, lat: 0, lng: 0 },
        Northeast: { elevation: -Infinity, lat: 0, lng: 0 },
        East: { elevation: -Infinity, lat: 0, lng: 0 },
        Southeast: { elevation: -Infinity, lat: 0, lng: 0 },
        South: { elevation: -Infinity, lat: 0, lng: 0 },
        Southwest: { elevation: -Infinity, lat: 0, lng: 0 },
        West: { elevation: -Infinity, lat: 0, lng: 0 },
        Northwest: { elevation: -Infinity, lat: 0, lng: 0 },
    };

    // Calculate the cardinal direction for each point
    for (const point of points) {
        const { latitude, longitude, elevation } = point;
        const angle = calculateAngleWithPolygonDirection(latitude, longitude, polygonCoordinates);
        const direction = determineCardinalDirection(angle);

        if (elevation > highestPoints[direction].elevation) {
            highestPoints[direction] = { elevation, lat: latitude, lng: longitude };
        }
    }

    return highestPoints;
}

export function findLowestDirectionalPoints(points: CoordinatesWithElevationVer2[], polygonCoordinates: Coordinates[]) {
    const lowestPoints = {
        North: { elevation: Infinity, lat: 0, lng: 0 },
        Northeast: { elevation: Infinity, lat: 0, lng: 0 },
        East: { elevation: Infinity, lat: 0, lng: 0 },
        Southeast: { elevation: Infinity, lat: 0, lng: 0 },
        South: { elevation: Infinity, lat: 0, lng: 0 },
        Southwest: { elevation: Infinity, lat: 0, lng: 0 },
        West: { elevation: Infinity, lat: 0, lng: 0 },
        Northwest: { elevation: Infinity, lat: 0, lng: 0 },
    };

    // Calculate the cardinal direction for each point
    for (const point of points) {
        const { latitude, longitude, elevation } = point;
        const angle = calculateAngleWithPolygonDirection(latitude, longitude, polygonCoordinates);
        const direction = determineCardinalDirection(angle);

        // console.log('test check ', { point: point, angle: angle, direction: direction });
        if (elevation < lowestPoints[direction].elevation) {
            lowestPoints[direction] = { elevation, lat: latitude, lng: longitude };
        }
    }

    return lowestPoints;
}

function calculateAngleWithPolygonDirection(latitude: number, longitude: number, polygonCoordinates: Coordinates[]) {
    let minDistance = Infinity;
    let nearestEdge = null;

    // Find the nearest edge in the polygon to the given point
    for (let i = 0; i < polygonCoordinates.length; i++) {
        const startVertex = polygonCoordinates[i];
        const endVertex = polygonCoordinates[(i + 1) % polygonCoordinates.length];
        const distance = pointToLineDistance(
            latitude,
            longitude,
            startVertex.lat,
            startVertex.lng,
            endVertex.lat,
            endVertex.lng,
        );
        if (distance < minDistance) {
            minDistance = distance;
            nearestEdge = { startVertex, endVertex };
        }
    }

    // Calculate the angle between the nearest edge and the given point
    const dx = nearestEdge && nearestEdge.endVertex.lng - nearestEdge.startVertex.lng;
    const dy = nearestEdge && nearestEdge.endVertex.lat - nearestEdge.startVertex.lat;
    const angle = dy && dx && Math.atan2(dy, dx) * (180 / Math.PI);

    if (angle) return angle;
    else return 0;
}

function pointToLineDistance(x: number, y: number, x1: number, y1: number, x2: number, y2: number) {
    const A = x - x1;
    const B = y - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    if (lenSq !== 0) param = dot / lenSq;

    let xx, yy;
    if (param < 0) {
        xx = x1;
        yy = y1;
    } else if (param > 1) {
        xx = x2;
        yy = y2;
    } else {
        xx = x1 + param * C;
        yy = y1 + param * D;
    }

    const dx = x - xx;
    const dy = y - yy;
    return Math.sqrt(dx * dx + dy * dy);
}

function determineCardinalDirection(angle: number) {
    // Normalize the angle to be within the range of 0 to 360 degrees
    const normalizedAngle = (angle + 360) % 360;

    // Determine the cardinal direction based on the normalized angle
    if (normalizedAngle >= 22.5 && normalizedAngle < 67.5) {
        return 'Northeast';
    } else if (normalizedAngle >= 67.5 && normalizedAngle < 112.5) {
        return 'East';
    } else if (normalizedAngle >= 112.5 && normalizedAngle < 157.5) {
        return 'Southeast';
    } else if (normalizedAngle >= 157.5 && normalizedAngle < 202.5) {
        return 'South';
    } else if (normalizedAngle >= 202.5 && normalizedAngle < 247.5) {
        return 'Southwest';
    } else if (normalizedAngle >= 247.5 && normalizedAngle < 292.5) {
        return 'West';
    } else if (normalizedAngle >= 292.5 && normalizedAngle < 337.5) {
        return 'Northwest';
    } else {
        return 'North';
    }
}
